/**
 * Staged hosts — the room's own furniture that staging draws differently
 * while it is dressed: a scanned bed drawn as the built-in bed, `bare`, with
 * the style's headboard, so the kit fits its mattress (a sofa will join it).
 *
 * The swap is a ROOM field, never an edit to the host:
 *
 *   room.stagedHosts = [{ objectId, kind: 'bed', headboard: { kind, color } }]
 *
 * written by `style_room_decor` beside the props and cleared with them. The
 * renderer DERIVES "this bed is staged" from it at draw time, the way dorm
 * derives bedding from `dormThemeId`. Nothing on the bed changes, so taking
 * the field away IS the restore: the stock bed comes back exactly as it was,
 * however the bed was edited in between (Megan's condition, 2026-10-28).
 *
 * A host counts as staged only while staged props still stand on it
 * (`parents` naming it). The swap exists to fit the kit, so a bed with no kit
 * on it has nothing to fit — and several paths strip staged props (Remove
 * accessories, a re-stage, a kitchen or great-room layout apply), so a path
 * that strips the props and forgets the field still restores the bed.
 */

const { isStagingDecor } = require('./ownership');
const { internByValue } = require('./internByValue');

/** The kinds of host a staging kit may swap. */
const STAGED_HOST_KINDS = new Set(['bed']);

/** Does a staged prop stand on (name as a parent) this object? */
const hostIsDressed = (objects, objectId) =>
  (objects || []).some(
    (o) =>
      isStagingDecor(o) && (o.parents || []).some((p) => p?.id === objectId)
  );

/**
 * The room's staged-host record for one object, or null when it is not staged.
 *
 * @param {{ objects?: object[], stagedHosts?: object[] }|null} room
 * @param {string} objectId
 * @returns {{ objectId: string, kind: string }|null}
 */
function stagedHostFor(room, objectId) {
  if (!room || objectId == null) return null;
  const entry = (room.stagedHosts || []).find(
    (h) => h && h.objectId === objectId && STAGED_HOST_KINDS.has(h.kind)
  );
  if (!entry) return null;
  return hostIsDressed(room.objects, objectId) ? entry : null;
}

// The headboard spec, interned so the renderer's options keep their identity
// across room snapshots (`./internByValue.js`).
const internHeadboard = (headboard) =>
  headboard?.kind
    ? internByValue({ kind: headboard.kind, color: headboard.color || null })
    : null;

/**
 * The bed renderer's staged options for this object: `bare` (the kit supplies
 * the bedding) and the style's `headboard`. Both absent for a bed that is not
 * staged, which draws exactly as it always has.
 *
 * @returns {{ bare: boolean, headboard: object|null }}
 */
function stagedBedRenderOptions(room, objectId) {
  const host = stagedHostFor(room, objectId);
  if (host?.kind !== 'bed') return { bare: false, headboard: null };
  return { bare: true, headboard: internHeadboard(host.headboard) };
}

/** The room with its staged hosts cleared, as a NEW room. */
const withoutStagedHosts = (room) => ({ ...room, stagedHosts: [] });

module.exports = {
  STAGED_HOST_KINDS,
  stagedHostFor,
  stagedBedRenderOptions,
  withoutStagedHosts,
};
